import React from 'react'
import { Accordion, AccordionDetails,AccordionSummary } from '@mui/material'
import {   IconCheck,
IconChevronDown,   IconClock,
  IconCopy,
  IconExclamationCircle,
  IconFileText,
  IconLoader,
  IconPlus,
  IconRefresh,
IconX } from '@tabler/icons-react'
import styled from 'styled-components'

import { IEditProduct } from 'types/products'
import { TRANSLATION_STATUS,TranslationProgress  } from 'types/translations'

import { TranslationProgressContent } from './TranslationProgressContent'

interface NotificationAccordionProps {
  progressData: TranslationProgress
  productData?: IEditProduct | null
  isBatch?: boolean
  displayName?: string
  onRemove?: () => void
  defaultExpanded?: boolean
}

export const NotificationAccordion: React.FC<NotificationAccordionProps> = ({
  progressData,
  productData,
  isBatch = false,
  displayName,
  onRemove,
  defaultExpanded = false
}) => {
  const getStatusIcon = (status: string) => {
    switch (status) {
      case TRANSLATION_STATUS.completed:
        return <IconCheck size={20} color='#814f99' />
      case TRANSLATION_STATUS.failed:
        return <IconExclamationCircle size={20} color='#814f99' />
      case TRANSLATION_STATUS.pending:
        return <IconClock size={20} color='#814f99' />
      case TRANSLATION_STATUS.validating:
        return <IconFileText size={20} color='#814f99' />
      case TRANSLATION_STATUS.translating:
        return <IconLoader size={20} color='#814f99' />
      case TRANSLATION_STATUS.copying_files:
        return <IconCopy size={20} color='#814f99' />
      case TRANSLATION_STATUS.creating_product:
        return <IconPlus size={20} color='#814f99' />
      case TRANSLATION_STATUS.translating_assets:
        return <IconLoader size={20} color='#814f99' />
      case TRANSLATION_STATUS.finalizing:
        return <IconRefresh size={20} color='#814f99' />
      default:
        return <IconClock size={20} color='#814f99' />
    }
  }

  const isCompleted = progressData.status === TRANSLATION_STATUS.completed
  const isFailed = progressData.status === TRANSLATION_STATUS.failed
  const showRemoveButton = isCompleted || isFailed

  const translationLabel = `${progressData.sourceCountry} → ${progressData.targetCountry}`

  return (
    <StyledAccordion defaultExpanded={defaultExpanded}>
      <StyledAccordionSummary
        expandIcon={<IconChevronDown size={20} />}
        aria-controls={`notification-${progressData.id}-content`}
        id={`notification-${progressData.id}-header`}
      >
        <AccordionHeaderContent>
          <AccordionHeaderLeft>
            <StatusIcon>{getStatusIcon(progressData.status)}</StatusIcon>
            {!isFailed && (
              <ProgressPercentage>{progressData.progress}%</ProgressPercentage>
            )}
            <TranslationLabel>{translationLabel}</TranslationLabel>
          </AccordionHeaderLeft>
          {showRemoveButton && onRemove && (
            <RemoveButton
              onClick={e => {
                e.stopPropagation()
                onRemove()
              }}
              aria-label='Remove notification'
            >
              <IconX size={18} />
            </RemoveButton>
          )}
        </AccordionHeaderContent>
      </StyledAccordionSummary>
      <StyledAccordionDetails>
        <TranslationProgressContent
          translationProgress={progressData}
          isBatch={isBatch}
          productData={productData}
          displayName={displayName}
        />
      </StyledAccordionDetails>
    </StyledAccordion>
  )
}

const StyledAccordion = styled(Accordion)`
  &:before {
    display: none;
  }
  box-shadow: none;
  border: 1px solid ${({ theme }) => theme.colors.muiBorder};
  border-radius: 8px !important;
  margin-bottom: 12px !important;

  &.Mui-expanded {
    margin-bottom: 12px !important;
  }
`

const StyledAccordionSummary = styled(AccordionSummary)`
  background-color: ${({ theme }) => theme.colors.background};
  border-radius: 8px;
  min-height: 56px !important;
  padding: 0 16px;

  &.Mui-expanded {
    min-height: 56px !important;
    border-bottom-left-radius: 0;
    border-bottom-right-radius: 0;
  }

  & .MuiAccordionSummary-content {
    margin: 0;
    align-items: center;
  }

  & .MuiAccordionSummary-expandIconWrapper {
    color: ${({ theme }) => theme.colors.muiLabel};
  }
`

const StyledAccordionDetails = styled(AccordionDetails)`
  padding: 0 !important;
  border-top: 1px solid ${({ theme }) => theme.colors.muiBorder};
`

const AccordionHeaderContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  gap: 12px;
`

const AccordionHeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
  min-width: 0;
`

const StatusIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`

const TranslationLabel = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSizeNormal};
  font-weight: ${({ theme }) => theme.typography.fontWeightBold};
  color: ${({ theme }) => theme.colors.menuLabel};
  white-space: nowrap;
`

const ProgressPercentage = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSizeSmall};
  font-weight: ${({ theme }) => theme.typography.fontWeightBold};
  color: #814f99;
  white-space: nowrap;
`

const RemoveButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border: none;
  background: transparent;
  border-radius: 4px;
  cursor: pointer;
  color: ${({ theme }) => theme.colors.muiLabel};
  transition: all 0.2s ease;
  flex-shrink: 0;

  &:hover {
    background: ${({ theme }) => theme.colors.menuHover};
    color: ${({ theme }) => theme.colors.menuLabel};
  }
`
